import { Download, FileText } from 'lucide-react'
import { Badge, type ColorDeBadge } from '@/shared/components/Badge'

type InvoiceStatus = 'received' | 'in_review' | 'approved' | 'observed' | 'rejected' | string | undefined

export interface InvoiceStatusMeta {
  label: string
  color: ColorDeBadge
}

export interface InvoiceSharedRowItem {
  id: string
  status?: InvoiceStatus
  xml_url?: string | null
  pdf_url?: string | null
  validation_notes?: string | null
  supplier_invoice_number?: string | null
  invoice_uuid?: string | null
  fiscal_folio?: string | null
  invoice_issued_at?: string | null
}

interface InvoiceSharedCellsProps {
  invoice: InvoiceSharedRowItem
  index: number
  formatDate: (value: string) => string
  onViewLines: () => void
  statusMeta: InvoiceStatusMeta
  /**
   * Estado del CFDI de complemento (C-09), cuando hay algo que señalar.
   *
   * Va aparte del estatus de la factura y no en su lugar: son dos cosas
   * distintas. Una factura Aprobada puede estar pagada sin complemento, y
   * pisar una etiqueta con la otra escondería justo el dato que bloquea al
   * proveedor.
   */
  etiquetaComplemento?: { texto: string; color: ColorDeBadge } | null
  theme?: 'light' | 'dark'
}

export const getInvoiceStatusMeta = (status?: InvoiceStatus): InvoiceStatusMeta => {
  switch (status) {
    case 'approved':
      return { label: 'Aprobada', color: 'verde' }
    case 'rejected':
      return { label: 'Rechazada', color: 'rojo' }
    case 'observed':
      return { label: 'Observada', color: 'naranja' }
    case 'received':
      return { label: 'Recibida', color: 'azul' }
    default:
      return { label: 'En revision', color: 'azul' }
  }
}

export function InvoiceSharedCells({
  invoice,
  index,
  formatDate,
  onViewLines,
  statusMeta,
  etiquetaComplemento,
  theme = 'light',
}: InvoiceSharedCellsProps) {
  const isDark = theme === 'dark'
  const mainTextClass = isDark ? 'text-gray-900 dark:text-white' : 'text-gray-900'
  const mutedTextClass = isDark ? 'text-gray-500 dark:text-gray-400' : 'text-gray-500'
  const linkClass = isDark
    ? 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1'
    : 'text-blue-600 hover:text-blue-800 flex items-center gap-1'

  return (
    <>
      <td className={`px-6 py-4 text-sm ${mainTextClass}`}>
        <p className={`font-semibold ${mainTextClass}`}>
          {invoice.supplier_invoice_number || `Factura #${index + 1}`}
        </p>
        <p className={`text-xs mt-1 ${mutedTextClass}`}>
          UUID: {invoice.invoice_uuid || 'No disponible'}
        </p>
        <p className={`text-xs ${mutedTextClass}`}>
          Folio fiscal: {invoice.fiscal_folio || 'No disponible'}
        </p>
        <p className={`text-xs ${mutedTextClass}`}>
          Emision: {invoice.invoice_issued_at ? formatDate(invoice.invoice_issued_at) : 'No disponible'}
        </p>
      </td>
      <td className="px-6 py-4 text-left">
        <Badge color={statusMeta.color}>{statusMeta.label}</Badge>
        {etiquetaComplemento && (
          <div className="mt-1.5">
            <Badge color={etiquetaComplemento.color}>{etiquetaComplemento.texto}</Badge>
          </div>
        )}
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm ${mutedTextClass}`}>
        {invoice.xml_url ? (
          <a
            href={invoice.xml_url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            title="Descargar XML"
          >
            <Download className="w-4 h-4" />
            <span className="text-xs font-medium underline">Descargar</span>
          </a>
        ) : (
          <span className="text-xs text-red-600 font-medium">Falta XML</span>
        )}
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm ${mutedTextClass}`}>
        {invoice.pdf_url ? (
          <a
            href={invoice.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            title="Ver PDF"
          >
            <FileText className="w-4 h-4" />
            <span className="text-xs font-medium underline">Descargar</span>
          </a>
        ) : (
          <span className="text-xs text-red-600 font-medium">Falta PDF</span>
        )}
      </td>
      <td className={`px-6 py-4 text-sm max-w-xs break-words ${mutedTextClass}`}>
        {invoice.validation_notes || <span className="text-gray-400">N/A</span>}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {!invoice.xml_url || !invoice.pdf_url ? (
          <Badge color="rojo">Archivo incompleto</Badge>
        ) : (
          <Badge color="verde">Completo</Badge>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <button
          className={isDark
            ? 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium cursor-pointer'
            : 'text-blue-600 hover:text-blue-800 font-medium cursor-pointer'}
          onClick={onViewLines}
        >
          Ver partidas
        </button>
      </td>
    </>
  )
}
